import { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { X, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { Button } from './Button';

// ── Animations ────────────────────────────────────────────────────────────────

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideIn = keyframes`from { opacity: 0; transform: translateY(-12px) scale(0.97); } to { opacity: 1; transform: none; }`;

// ── Shared overlay ────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${({ theme }) => theme.zIndices.modalBackdrop};
  animation: ${fadeIn} 150ms ease;
  padding: 1rem;
`;

const Box = styled.div`
  background: white;
  border-radius: 10px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.18);
  animation: ${slideIn} 180ms ease;
  overflow: hidden;
`;

const Body = styled.div`
  padding: 1.75rem 1.75rem 1.25rem;
`;

const IconRow = styled.div<{ $variant: 'danger' | 'success' | 'info' }>`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  background: ${({ $variant }) =>
    $variant === 'danger' ? '#fee2e2'
    : $variant === 'success' ? '#dcfce7'
    : '#dbeafe'};
  color: ${({ $variant }) =>
    $variant === 'danger' ? '#dc2626'
    : $variant === 'success' ? '#16a34a'
    : '#2563eb'};
`;

const Title = styled.h3`
  font-size: 1.05rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem;
`;

const Message = styled.p`
  font-size: 0.9rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.75rem 1.5rem;
`;

// ── Toast ─────────────────────────────────────────────────────────────────────

const toastSlide = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: none; }
`;

const ToastWrapper = styled.div<{ $variant: 'success' | 'error' | 'info' }>`
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: ${({ theme }) => theme.zIndices.tooltip};
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1.25rem;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
  animation: ${toastSlide} 200ms ease;
  white-space: nowrap;
  font-size: 0.9rem;
  font-weight: 500;
  background: ${({ $variant }) =>
    $variant === 'success' ? '#166534'
    : $variant === 'error' ? '#991b1b'
    : '#1e3a8a'};
  color: white;

  svg { flex-shrink: 0; }
`;

const ToastClose = styled.button`
  background: none;
  border: none;
  color: rgba(255,255,255,0.75);
  cursor: pointer;
  padding: 0;
  margin-left: 0.5rem;
  display: flex;
  &:hover { color: white; }
`;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'info';
  onConfirm: () => void;
  onCancel: () => void;
}

export interface ToastOptions {
  message: string;
  variant: 'success' | 'error' | 'info';
  onClose: () => void;
}

// ── ConfirmDialog ─────────────────────────────────────────────────────────────

export const ConfirmDialog = ({
  title,
  message,
  confirmLabel = 'Bevestigen',
  cancelLabel = 'Annuleer',
  variant = 'danger',
  onConfirm,
  onCancel,
}: ConfirmOptions) => {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onCancel]);

  return (
    <Overlay onClick={onCancel}>
      <Box onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal>
        <Body>
          <IconRow $variant={variant}>
            {variant === 'danger' ? <AlertTriangle size={22} /> : <Info size={22} />}
          </IconRow>
          <Title>{title}</Title>
          <Message>{message}</Message>
        </Body>
        <Footer>
          <Button ref={cancelRef} variant="ghost" onClick={onCancel}>{cancelLabel}</Button>
          <Button
            onClick={onConfirm}
            style={variant === 'danger' ? { background: '#dc2626', borderColor: '#dc2626' } : {}}
          >
            {confirmLabel}
          </Button>
        </Footer>
      </Box>
    </Overlay>
  );
};

// ── Toast ─────────────────────────────────────────────────────────────────────

export const Toast = ({ message, variant, onClose }: ToastOptions) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  const Icon = variant === 'success' ? CheckCircle : variant === 'error' ? AlertTriangle : Info;

  return (
    <ToastWrapper $variant={variant}>
      <Icon size={18} />
      {message}
      <ToastClose onClick={onClose} aria-label="Sluiten"><X size={16} /></ToastClose>
    </ToastWrapper>
  );
};
